import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { formatMoney } from "../lib/format";
import { EstimateTable, Stat, MetaGrid } from "../components/EstimateTable";

export function PwsProfile() {
  const { id = "" } = useParams();
  const r = useQuery({ queryKey: ["pws", id], queryFn: () => api.pwsProfile(Number(id)) });

  if (r.isLoading) return <div className="text-slate-500">Loading...</div>;
  if (!r.data || r.error) return <div className="text-red-600">{(r.error as Error)?.message ?? "Not found"}</div>;

  const p = r.data.pws;
  const k = r.data.kpis ?? {};
  const rag = p.partnership_rag?.toLowerCase();
  const ragBg = rag === "red" ? "bg-liaise-red/15 text-red-700"
              : rag === "amber" ? "bg-liaise-orange/15 text-orange-800"
              : rag === "green" ? "bg-liaise-green/20 text-emerald-800"
              : "bg-slate-100 text-slate-600";

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-liaise-navy">{p.first_name} {p.last_name} <span className="text-slate-400">({p.initials})</span></h1>
          <div className="text-sm text-slate-500 mt-1">
            {p.primary_home_id ? <Link className="text-liaise-blue hover:underline" to={`/home/${p.primary_home_id}`}>{p.primary_home_name}</Link> : "-"}
            {" "}· {p.primary_home_region ?? "-"}
            {" "}· Host authority {p.host_authority ?? "-"}
            {" "}· {p.resi_or_sl ?? "-"}
          </div>
        </div>
        <Link to="/all-estimates" className="text-sm text-slate-500 hover:underline">← Back</Link>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 p-6">
        <MetaGrid items={[
          ["Funder", p.primary_funder_id ? <Link to={`/funder/${p.primary_funder_id}`} className="text-liaise-blue hover:underline">{p.primary_funder_name}</Link> : "-"],
          ["Secondary funder", p.secondary_funder_id ? <Link to={`/funder/${p.secondary_funder_id}`} className="text-liaise-blue hover:underline">{p.secondary_funder_name}</Link> : <span className="text-slate-400">—</span>],
          ["Acuity", p.acuity ?? "-"],
          ["Weekly fee", formatMoney(p.weekly_fee)],
          ["Hours per week", p.hours_per_week ?? "-"],
          ["Hourly fee", formatMoney(p.hourly_fee)],
          ["Annual contribution", <span>{formatMoney(p.annual_contribution)} {p.contribution_pct != null && <span className="text-xs text-slate-500 font-normal">({Number(p.contribution_pct).toFixed(1)}%)</span>}</span>],
          ["Annual EBITDA", <span>{formatMoney(p.annual_ebitda)} {p.ebitda_pct != null && <span className="text-xs text-slate-500 font-normal">({Number(p.ebitda_pct).toFixed(1)}%)</span>}</span>],
          ["Dashboard", <>{p.dashboard_score ?? "-"} / 18</>],
        ]} />
        {rag && (
          <div className={`mt-4 inline-block text-xs px-2 py-1 rounded-full ${ragBg}`}>
            Partnership RAG: {p.partnership_rag}
          </div>
        )}
      </section>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Total" value={String(k.total ?? 0)} />
        <Stat label="Drafts" value={String(k.drafts ?? 0)} />
        <Stat label="In review" value={String(k.submitted ?? 0)} highlight={(k.submitted ?? 0) > 0} />
        <Stat label="Approved" value={String(k.approved ?? 0)} />
        <Stat label="Approved £" value={formatMoney(k.approved_value)} />
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 text-sm font-semibold text-liaise-navy">Estimates for this PWS</div>
        <EstimateTable rows={r.data.estimates ?? []} hide={["pws"]} />
      </section>
    </div>
  );
}
